import type { CategoryData } from 'service/category/model.ts';
import { computed, Signal } from '@preact/signals';
import { activeCategory, categoriesMap } from 'signals/category';
import { tokenize } from 'utils/tokenize.ts';
import { localize } from 'i18n/localize.ts';
import { CATEGORY_ALL_CATEGORIES_SEO_NAME } from 'utils/constants';
import type { BazarCategoryTreeCategory } from 'service/category/utils.ts';

const $t = localize();

export type CategoryTreeCategory = CategoryData & {
	children: CategoryTreeCategory[];
	parent: CategoryTreeCategory | null;
	searchToken: string;
};

export type DisplayCategory = CategoryTreeCategory & { offersCount?: number | undefined };

export const CATEGORY_TREE_ALL_CATEGORIES: CategoryTreeCategory = {
	id: 0,
	seoName: CATEGORY_ALL_CATEGORIES_SEO_NAME,
	title: $t.categories.allOffers,
	name: $t.categories.allOffers,
	children: [],
	parent: null,
	searchToken: '',
	level: 0,
	parentId: null,
	path: [],
	seoKeywords: '',
};

export type CategoryTree = Record<number, CategoryTreeCategory>;

export function categoryTreeToCategory(categoryTree: BazarCategoryTreeCategory): CategoryData {
	const { children, parent, offersCount, ...category } = categoryTree;
	return category;
}

export const categoryModalOpen = new Signal<boolean>(false);

export function closeModal() {
	categoryModalOpen.value = false;
}

export function openModal() {
	categoryModalOpen.value = true;
}

// Subset of categories that are relevant to search (root -> Auto-Moto => search only children of Auto-Moto)
const categoriesToSearchThrough = computed(() => {
	if (activeCategory.value) {
		return gatherChildren(activeCategory.value, []);
	}

	return Object.values(categoriesMap.value);
});
function gatherChildren(category: BazarCategoryTreeCategory, acc: BazarCategoryTreeCategory[]) {
	acc.push(...category.children);
	category.children.forEach((child) => gatherChildren(child, acc));

	return acc;
}

export const searchPhrase = new Signal<string>('');
export const filteredCategories = computed(() => {
	if (!searchPhrase.value || searchPhrase.value.trim().length === 0) {
		return null;
	}

	const searchPhraseValue = tokenize(searchPhrase.value);

	return (
		categoriesToSearchThrough.value
			// @ts-expect-error todo: due to the fact that this signal is used in multiple places, the types are not perfect. Homepage is the only page that requires searchToken to be present in the category, and it is guaranteed for that page
			?.filter(({ searchToken }) => searchToken.includes(searchPhraseValue))
			.sort((a, b) => ((a.path?.length ?? -1) >= (b.path?.length ?? -1) ? 1 : -1))
	);
});

globalThis?.document?.addEventListener('astro:before-swap', () => {
	activeCategory.value = null;
});

export function activateCategory(
	category: BazarCategoryTreeCategory | null | number,
	{ onClose = () => {} }: { onClose?: () => void } = {},
) {
	if (typeof category === 'number') {
		category = categoriesMap.peek()[category] ?? null;
	}

	if (category === null || category === undefined) {
		activeCategory.value = null;
		onClose();
		return;
	}
	activeCategory.value = category;

	if (category) {
		if (!category.children?.length) {
			onClose();
		}
	}
}

export function activateParentCategory() {
	const _activeCategory = activeCategory.peek();

	if (_activeCategory !== null) {
		const parent = _activeCategory.parent;

		activeCategory.value = parent;
	}
}
